import { useCallback } from 'react';
import { AreaClosed } from '@visx/shape';
import type { D3Scale } from '@visx/scale';
import { curveMonotoneX, CurveFactory } from 'd3-shape';
import type { ScaleLinear, ScaleTime } from 'd3-scale';
import { bisector } from 'd3-array';
import { getSafeZero } from '../utils';

const bisectDateLeft = bisector((d) => d).left;
const bisectDateRight = bisector((d) => d).right;

interface Props {
  data: number[];
  xSelect: (date: number) => number;
  ySelect: (date: number) => number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  fillPos?: string;
  fillNeg?: string;
  startX?: number;
  endX?: number;
  curve?: CurveFactory;
}

const Area = ({
  curve,
  data,
  endX,
  fillNeg,
  fillPos = 'white',
  startX,
  xScale,
  xSelect,
  yScale,
  ySelect,
}: Props) => {
  let start = startX || xScale.domain()[0].getTime();
  let end = endX || xScale.domain()[1].getTime();

  const yScaleDomain = yScale.domain();

  const clipStart = xScale(start) as number;
  const clipStop = (xScale(end) as number) - clipStart;
  const clipId = `area-path-clipping-mask-${start}-${end}-${yScaleDomain[1]}-${yScaleDomain[0]}`;

  const indexLeft = bisectDateLeft(data, start);
  const indexRight = bisectDateRight(data, end);
  start = data[indexLeft - 1] || start;
  end = data[indexRight] || end;

  const zero = getSafeZero(yScale);
  const defined = useCallback(
    (d) => {
      const x = xSelect(d);
      const y = ySelect(d);
      return y !== undefined && x >= start && x <= end;
    },
    [xSelect, ySelect, start, end]
  );

  return (
    <>
      <svg y="0" x="0" width="100%" height="100%">
        <defs>
          <clipPath id={clipId}>
            <rect
              x={clipStart}
              y="0"
              width={Math.abs(clipStop)}
              height="100%"
            />
          </clipPath>
        </defs>
      </svg>
      {yScale.domain()[1] > 0 && (
        <svg y="0" x="0" width="100%" height={zero}>
          <AreaClosed
            data={data}
            x={(d) => xScale(xSelect(d)) as number}
            y={(d) => yScale(ySelect(d)) as number}
            y0={() => zero}
            yScale={yScale as D3Scale<number, number>} // This prop is useless but VX TS required it
            fill={fillPos}
            fillOpacity="0.5"
            defined={defined}
            curve={curve || curveMonotoneX}
            clipPath={`url(#${clipId})`}
          />
        </svg>
      )}
      {yScale.domain()[0] < 0 && (
        <svg y={zero} x="0" width="100%" height="100%">
          <AreaClosed
            x={(d) => xScale(xSelect(d)) as number}
            y={(d) => (yScale(ySelect(d)) as number) - zero}
            data={data}
            y0={() => 0}
            yScale={yScale as D3Scale<number, number>} // This prop is useless but VX TS required it
            fill={fillNeg || fillPos}
            fillOpacity="0.5"
            defined={defined}
            curve={curve || curveMonotoneX}
            clipPath={`url(#${clipId})`}
          />
        </svg>
      )}
    </>
  );
};

export default Area;
